import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DynamicSelect from "../../DynamicSelect";
import { ExpertPostingPrintSlip } from "../ExpertPostingPrintSlip";
import Pagination from "../../Shared/Pagination";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const POSTING_API = "https://api.muktihospital.com/api/pathology-postings";
const PERFORMER_API = "https://api.muktihospital.com/api/pathology-performers";
const LIVE_CASH_API = "https://api.muktihospital.com/api/pathology-accounts/live-cash";

export default function PathologyExpertPosting() {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("pending"); // "pending" | "posted"
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [performers, setPerformers] = useState([]);
  const [selectedPerformer, setSelectedPerformer] = useState(null);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ count: 0, total: 0, totalEarned: 0 });
  const [loading, setLoading] = useState(false);
  const [postingId, setPostingId] = useState(null);
  const [unpostRow, setUnpostRow] = useState(null);

  // posted-tab account info
  const [account, setAccount] = useState({ totalEarned: 0, totalPaid: 0, balanceDue: 0, postedCount: 0 });
  const [payouts, setPayouts] = useState([]);

  // payout modal
  const [payOpen, setPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");
  const [paying, setPaying] = useState(false);
  const [cashInHand, setCashInHand] = useState(null);
  const [loadingCash, setLoadingCash] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const handlePrintSlip = () => {
    window.print();
  };

  // 1) load all performers for the top dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(PERFORMER_API, {
          headers: { "x-api-key": API_KEY },
        });
        setPerformers(res.data.data || []);
      } catch (err) {
        console.error("Load performers error:", err);
        toast.error("Failed to load performers.");
      }
    })();
  }, []);

  // load the account summary + payout history (posted tab)
  const fetchAccount = useCallback(async () => {
    if (!selectedPerformer) {
      setAccount({ totalEarned: 0, totalPaid: 0, balanceDue: 0, postedCount: 0 });
      setPayouts([]);
      return;
    }
    try {
      const res = await axios.get(
        `${POSTING_API}/account/${selectedPerformer.value}`,
        { headers: { "x-api-key": API_KEY } }
      );
      setAccount(res.data.summary || { totalEarned: 0, totalPaid: 0, balanceDue: 0, postedCount: 0 });
      setPayouts(res.data.payouts || []);
    } catch (err) {
      console.error("Fetch account error:", err);
    }
  }, [selectedPerformer]);

  const fetchLiveCash = useCallback(async () => {
    setLoadingCash(true);
    try {
      const res = await axios.get(LIVE_CASH_API, {
        headers: { "x-api-key": API_KEY },
        params: { userName: user?.name || "" }
      });
      setCashInHand(res.data.availableCash ?? 0);
    } catch (err) {
      console.error("Fetch live cash error:", err);
      setCashInHand(0);
    } finally {
      setLoadingCash(false);
    }
  }, [user?.name]);

  useEffect(() => {
    fetchLiveCash();
  }, [fetchLiveCash]);

  // 2) load list based on active tab
  const fetchData = useCallback(async () => {
    if (!selectedPerformer) {
      setRows([]);
      setSummary({ count: 0, total: 0, totalEarned: 0 });
      return;
    }
    setLoading(true);
    try {
      const endpoint =
        activeTab === "pending"
          ? `${POSTING_API}/by-performer/${selectedPerformer.value}`
          : `${POSTING_API}/posted/${selectedPerformer.value}`;
      const res = await axios.get(endpoint, {
        headers: { "x-api-key": API_KEY },
      });
      setRows(res.data.data || []);
      setSummary(res.data.summary || { count: 0, total: 0, totalEarned: 0 });
    } catch (err) {
      console.error("Fetch data error:", err);
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [selectedPerformer, activeTab]);

  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return rows.slice(startIndex, startIndex + itemsPerPage);
  }, [rows, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchData();
    if (activeTab === "posted") fetchAccount();
  }, [selectedPerformer, activeTab, fetchData, fetchAccount]);

  const handlePost = async (row) => {
    setPostingId(row.id);
    try {
      await axios.post(
        `${POSTING_API}/${row.id}/post`,
        { performerId: selectedPerformer.value, userName: user?.name || "Unknown Staff" },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Commission posted successfully.");
      fetchData();
    } catch (err) {
      console.error("Post error:", err);
      toast.error(err.response?.data?.error || "Failed to post.");
    } finally {
      setPostingId(null);
    }
  };

  const handleUnpost = async () => {
    if (!unpostRow) return;
    const row = unpostRow;
    setPostingId(row.id);
    try {
      await axios.post(`${POSTING_API}/${row.id}/unpost`, {}, { headers: { "x-api-key": API_KEY } });
      toast.success("Moved back to pending.");
      setUnpostRow(null);
      fetchData();
      fetchAccount();
    } catch (err) {
      console.error("Unpost error:", err);
      toast.error(err.response?.data?.error || "Failed to unpost.");
    } finally {
      setPostingId(null);
    }
  };

  const handlePay = async () => {
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) {
      toast.warn("Enter a valid amount.");
      return;
    }

    if (cashInHand !== null && (cashInHand <= 0 || amt > cashInHand)) {
      return toast.error(`Insufficient Cash in Hand! Available: ৳${cashInHand.toFixed(2)}, Requested: ৳${amt.toFixed(2)}`);
    }

    setPaying(true);
    try {
      await axios.post(
        `${POSTING_API}/payout`,
        {
          performerId: selectedPerformer.value,
          amount: amt,
          note: payNote,
          userName: user?.name || "Unknown Staff",
        },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Payout recorded successfully.");
      setPayOpen(false);
      setPayAmount("");
      setPayNote("");
      fetchAccount();
      fetchLiveCash();
    } catch (err) {
      console.error("Payout error:", err);
      toast.error(err.response?.data?.error || "Failed to record payout.");
    } finally {
      setPaying(false);
    }
  };

  const performerOptions = performers.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.type})`,
  }));

  const TabButton = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        activeTab === id
          ? "border-M-primary-color text-M-primary-color"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      <ToastContainer position="top-right" autoClose={2500} />
      <div className="bg-white shadow-sm rounded-lg">
        {/* Title + performer picker */}
        <div className="border-b border-dashed border-M-text-color/50 p-4">
          <h2 className="text-base font-medium text-gray-700 mb-3">
            Expense Posting — Doctor / Technician Test Commission
          </h2>
          <div className="w-full sm:w-80">
            <DynamicSelect
              options={performerOptions}
              value={selectedPerformer}
              onChange={(o) => setSelectedPerformer(o || null)}
              isSearchable={true}
              isClearable={true}
              placeholder="Select doctor / technician..."
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-4 border-b border-gray-200">
          <TabButton id="pending" label="Pending" />
          <TabButton id="posted" label="Posted & Payout" />
        </div>

        {!selectedPerformer ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            Select a performer above to continue.
          </div>
        ) : (
          <>
            {/* POSTED TAB: account summary cards */}
            {activeTab === "posted" && (
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border-l-4 border-emerald-500 rounded-lg p-3 shadow-sm">
                  <p className="text-[10px] font-black uppercase text-gray-400">Total Earned</p>
                  <p className="text-xl font-black text-emerald-600">৳{Number(account.totalEarned).toFixed(2)}</p>
                </div>
                <div className="bg-white border-l-4 border-blue-500 rounded-lg p-3 shadow-sm">
                  <p className="text-[10px] font-black uppercase text-gray-400">Total Paid</p>
                  <p className="text-xl font-black text-blue-600">৳{Number(account.totalPaid).toFixed(2)}</p>
                </div>
                <div className="bg-white border-l-4 border-amber-500 rounded-lg p-3 shadow-sm">
                  <p className="text-[10px] font-black uppercase text-gray-400">Balance Due</p>
                  <p className="text-xl font-black text-amber-600">৳{Number(account.balanceDue).toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      fetchLiveCash();
                      setPayOpen(true);
                    }}
                    disabled={Number(account.balanceDue) <= 0}
                    className="w-full bg-M-primary-color/90 hover:bg-M-primary-color text-white text-sm py-2.5 px-3 rounded-md disabled:opacity-50 whitespace-nowrap"
                  >
                    Pay Performer
                  </button>
                  <button
                    onClick={handlePrintSlip}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white text-sm py-2.5 px-3 rounded-md shadow-sm whitespace-nowrap"
                  >
                    Print Voucher
                  </button>
                </div>
              </div>
            )}

            {/* List table */}
            <div className="overflow-x-auto">
              <table className="min-w-[850px] w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left uppercase font-inter font-medium text-xs text-M-heading-color p-3">
                      {activeTab === "pending" ? "Date" : "Posted On"}
                    </th>
                    <th className="text-left uppercase font-inter font-medium text-xs text-M-heading-color p-3">Bill No</th>
                    <th className="text-left uppercase font-inter font-medium text-xs text-M-heading-color p-3">Patient</th>
                    <th className="text-left uppercase font-inter font-medium text-xs text-M-heading-color p-3">Test</th>
                    <th className="text-left uppercase font-inter font-medium text-xs text-M-heading-color p-3">Commission</th>
                    <th className="text-center uppercase font-inter font-medium text-xs text-M-heading-color p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-sm">Loading...</td></tr>
                  ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-gray-400 text-sm">
                    {activeTab === "pending" ? "No pending tests. 🎉" : "No posted commission yet."}
                  </td>
                </tr>
              ) : (
                currentData.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-sm text-gray-600">
                          {activeTab === "pending"
                            ? new Date(row.createdAt).toLocaleDateString("en-GB")
                            : row.postedAt
                            ? new Date(row.postedAt).toLocaleDateString("en-GB")
                            : "N/A"}
                        </td>
                        <td className="p-3 text-sm font-semibold text-gray-800">{row.billNumber}</td>
                        <td className="p-3 text-sm text-gray-700 capitalize">{row.patientName || "N/A"}</td>
                        <td className="p-3 text-sm text-gray-700">{row.testName}</td>
                        <td className="p-3 text-sm font-bold text-emerald-600">৳{Number(row.commissionAmount).toFixed(2)}</td>
                        <td className="p-3 text-center">
                          {activeTab === "pending" ? (
                            <div className="flex flex-col items-center gap-1">
                              <button
                                onClick={() => handlePost(row)}
                                disabled={postingId === row.id}
                                className="bg-M-primary-color/90 hover:bg-M-primary-color text-white text-sm py-1.5 px-5 rounded-md disabled:opacity-50 whitespace-nowrap"
                              >
                                {postingId === row.id ? "Posting..." : "Post"}
                              </button>
                              {Number(row.billDue || 0) > 0 && (
                                <span className="text-[10px] text-amber-600 whitespace-nowrap">
                                  Bill due ৳{Number(row.billDue || 0).toFixed(0)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => setUnpostRow(row)}
                              disabled={postingId === row.id}
                              className="text-[11px] text-gray-500 border border-gray-300 hover:bg-gray-100 px-2 py-1 rounded-md disabled:opacity-50 whitespace-nowrap"
                              title="Move back to pending (if posted by mistake)"
                            >
                              {postingId === row.id ? "..." : "Unpost"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* POSTED TAB: payout history */}
            {activeTab === "posted" && payouts.length > 0 && (
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="text-sm font-bold text-gray-700">Payout History</h3>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-2.5 text-xs font-bold text-gray-600">Date</th>
                        <th className="text-left p-2.5 text-xs font-bold text-gray-600">Amount</th>
                        <th className="text-left p-2.5 text-xs font-bold text-gray-600">Note</th>
                        <th className="text-left p-2.5 text-xs font-bold text-gray-600">Paid By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p.id} className="border-b last:border-none">
                          <td className="p-2.5 text-gray-600">{new Date(p.createdAt).toLocaleDateString("en-GB")}</td>
                          <td className="p-2.5 font-bold text-blue-600">৳{Number(p.amount).toFixed(2)}</td>
                          <td className="p-2.5 text-gray-600">{p.note || "—"}</td>
                          <td className="p-2.5 text-gray-600">{p.paidByUserName || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Reusable Pagination Component */}
          {totalPages > 1 && (
            <div className="print-hidden">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={rows.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
          </>
        )}
      </div>

      {/* Unpost modal */}
      {unpostRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Unpost this commission?</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-sm text-gray-700 mb-3">
              <div><span className="text-gray-400">Test:</span> {unpostRow.testName}</div>
              <div><span className="text-gray-400">Bill:</span> {unpostRow.billNumber}</div>
              <div><span className="text-gray-400">Performer:</span> {unpostRow.performerName || "N/A"}</div>
              <div><span className="text-gray-400">Commission:</span> ৳{Number(unpostRow.commissionAmount).toFixed(2)}</div>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              This will move the commission <span className="font-semibold">back to Pending</span> and
              remove it from this performer's earned total.
            </p>
            <p className="text-xs text-gray-400 mb-5">
              Use this only if it was posted to the wrong performer or by mistake.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setUnpostRow(null)} className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleUnpost} disabled={postingId === unpostRow.id} className="px-4 py-2 text-sm rounded-md bg-[#E7633D] hover:bg-[#d9542e] text-white font-semibold disabled:opacity-50">
                {postingId === unpostRow.id ? "Unposting..." : "Yes, Unpost"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout modal */}
      {payOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Pay Performer</h3>
            <p className="text-sm text-gray-500 mb-4">
              {selectedPerformer?.label} · Balance Due:{" "}
              <span className="font-bold text-amber-600">৳{Number(account.balanceDue).toFixed(2)}</span>
            </p>
            <div className="flex justify-between items-center px-3 py-2 mb-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
              <span className="font-bold text-blue-900">Your Cash in Hand:</span>
              <span className={`font-black ${cashInHand <= 0 ? "text-red-600" : "text-emerald-700"}`}>
                {loadingCash ? "Checking..." : `৳ ${(cashInHand || 0).toFixed(2)}`}
              </span>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (৳)</label>
            <input
              type="number"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md outline-none focus:ring-0 text-sm font-bold ${
                cashInHand !== null && Number(payAmount) > cashInHand
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300"
              }`}
              placeholder={`Max ৳${Number(account.balanceDue).toFixed(2)}`}
              min="0"
            />
            {cashInHand !== null && Number(payAmount) > cashInHand && (
              <span className="text-[11px] text-red-500 font-bold block mt-1 mb-2">
                Payout amount exceeds available cash (৳{(cashInHand || 0).toFixed(2)})!
              </span>
            )}

            <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">Note (optional)</label>
            <input
              type="text"
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              className="w-full mb-5 px-3 py-2 border border-gray-300 rounded-md outline-none focus:ring-0 text-sm"
              placeholder="e.g. Cash paid for June"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setPayOpen(false)} className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button 
                onClick={handlePay} 
                disabled={
                  paying || 
                  loadingCash || 
                  (cashInHand !== null && (cashInHand <= 0 || Number(payAmount) > cashInHand))
                } 
                className="px-4 py-2 text-sm rounded-md bg-M-primary-color/90 hover:bg-M-primary-color text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {paying ? "Saving..." : "Confirm Payout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedPerformer && (
        <ExpertPostingPrintSlip
          className="print-slip-area"
          performer={selectedPerformer}
          account={account}
          postedList={rows}
          payouts={payouts}
          printedBy={user?.name || "Afroja Akhter"}
        />
      )}
    </div>
  );
}